import { endOfDay, formatRFC3339 } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { DateRangePicker, IconButton, Loading, SearchInput, Typography } from "@/components/atoms";
import { DataTable, Pagination } from "@/components/molecules";
import { DataTableSorting } from "@/components/molecules/DataTable/types";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { ACCESS_LIST_PAGE_SIZE, useGetAccessListQuery } from "@/redux/apis/assistants/assistantsApi";
import { useGetClientJobsQuery } from "@/redux/apis/booking/bookingApi";
import { GetBookingsParams } from "@/redux/apis/booking/types";
import { useGetClientBookingsQuery, useGetManagedBookingsQuery } from "@/redux/apis/clientBooking/clientBookingApi";
import { useAppSelector } from "@/redux/hooks";
import { accountIdSelector } from "@/redux/slices/account/selectors";
import { toClientBooking } from "./adapters/toClientBooking";
import { BookingHistoryEmptyState } from "./components/BookingHistoryEmptyState";
import { MobileDateDrawer } from "./components/MobileDateDrawer";
import {
  ActiveFilterPills,
  BookingHistoryFilterDrawer,
  BookingHistoryFilterState,
  BookingHistoryFilters,
} from "./components/filters/BookingHistoryFilters";
import { bookingHistoryColumns, managedClientColumn } from "./fixtures";
import { generateBookingHistoryData } from "./helpers";
import { getStatusesForGroup } from "./statusGroups";

const getDefaultDates = (): [Date, Date] => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  return [start, end];
};

interface BookingHistoryTableProps {
  /**
   * When set, fetch the managed client's jobs via the operator endpoint
   * (`/v3/provider/{op}/job` filtered by `client`) instead of the client's own
   * `/v2/cust/booking`. Used by the operator client-record Details tab.
   */
  clientId?: string;
  /**
   * Managed mode: fetch bookings MERGED across every client the assistant manages via
   * `/v2/cust/booking?scope=managed`, and render an extra "Client" column (the owning client).
   * Mutually exclusive with `clientId`; the default (neither) is the client self-service list.
   */
  managed?: boolean;
}

export const BookingHistoryTable = ({ clientId, managed = false }: BookingHistoryTableProps = {}) => {
  const isOperator = !!clientId;
  const { operatorId, accessId } = useParams<{ operatorId: string; accessId: string }>();
  // Client-portal OBO (e.g. EA Manage-Client takeover): when the tree is wrapped in ClientScopeProvider
  // the self-service query targets the managed account via account_uuid. Undefined in plain self-service.
  const { accountId: scopedAccountId } = useClientScope();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sorting, setSorting] = useState<DataTableSorting>([]);
  const [search, setSearch] = useState("");
  const [defaultStart, defaultEnd] = getDefaultDates();
  const [dateFrom, setDateFrom] = useState<Date>(defaultStart);
  const [dateTo, setDateTo] = useState<Date>(defaultEnd);
  const [filters, setFilters] = useState<BookingHistoryFilterState>({});
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  useEffect(() => setPage(1), [sorting, search, filters]);

  const isSearching = !!search;
  const sort = sorting[0];

  // Client self-service path — token-scoped /v2/cust/booking.
  // status: "all" returns past + upcoming in one server-sorted list (DEV-1251/DEV-1243);
  // it supersedes `past`, and composes with the search/date/passenger/state filters below.
  // The managed list (DEV-405) reuses the same params (minus account_uuid) — only the endpoint differs
  // (?scope=managed) — so both queries share one args object; `skip` picks the active one.
  const clientListArgs = {
    page,
    pageSize,
    /*
     * The stage the user picked, else "all" (past + upcoming in one list). Filtered server-side —
     * the list is paginated and sorted there, so narrowing it here would give wrong totals.
     */
    status: filters.status || "all",
    search: search || undefined,
    /*
     * DATES drop while searching, because the backend ignores them then anyway (see the "skip date
     * filters" branch in BookingResource) — sending them would only misrepresent what was asked.
     *
     * The pill filters below do NOT drop. Search and filters compose everywhere else in the product
     * — the operator Bookings page, the Clients page, and the legacy booking history, which submits
     * search as just another field of the same form. This table used to suppress them, which made a
     * pill sit there looking applied while it silently wasn't.
     */
    startDate: isSearching ? undefined : formatRFC3339(dateFrom),
    endDate: isSearching ? undefined : formatRFC3339(endOfDay(dateTo)),
    sort: sort?.id,
    order: sort?.desc ? ("desc" as const) : ("asc" as const),
    passenger: filters.passenger || undefined,
    state: filters.state?.length ? filters.state : undefined,
  };
  // Self-service / OBO path: account_uuid (scopedAccountId) targets the managed account under
  // ClientScopeProvider (EA Manage-Client takeover); undefined in plain self-service (DEV-1299/DEV-408).
  const clientQuery = useGetClientBookingsQuery({ ...clientListArgs, accountId: scopedAccountId }, { skip: isOperator || managed });

  // Managed merged path — /v2/cust/booking?scope=managed (no account_uuid).
  const managedQuery = useGetManagedBookingsQuery(clientListArgs, { skip: !managed });

  // Managed rows route through the EA's CONNECTED access links (the same list ClientPickerModal
  // offers), so each row can open its owning client's `book-for/:accessId` sheet. The backend also
  // appends the EA's OWN bookings to the managed list; those are told apart by the active account id.
  const activeAccountId = useAppSelector(accountIdSelector);
  // page_size: the endpoint pages at 12 by default, but the managed list is unbounded, so a 13th client's
  // rows would otherwise never find their link. Loading is folded into the table's spinner below so a
  // row can't be clicked before its scope is known.
  const accessQuery = useGetAccessListQuery({ type: "YOU_MANAGE", status: "CONNECTED", page_size: ACCESS_LIST_PAGE_SIZE }, { skip: !managed });
  const accessLinks = accessQuery.data;
  const accessIdByClient = useMemo(() => new Map((accessLinks ?? []).map((link) => [link.party.uuid, link.id])), [accessLinks]);

  // Operator client-record path — provider job endpoint filtered to this client.
  // Build params with only defined keys so createSearchParams never emits "undefined".
  const operatorParams: GetBookingsParams = useMemo(() => {
    const p: GetBookingsParams = {
      page: String(page),
      page_size: String(pageSize),
      direction: sort?.desc ? "desc" : "asc",
      client: clientId,
    };
    if (sort?.id) p.sort = sort.id;
    if (search) p.search = search;
    // Dates only — the backend drops them while searching, so sending them would misstate the query.
    if (!isSearching) {
      p.start_date_time = formatRFC3339(dateFrom);
      p.end_date_time = formatRFC3339(endOfDay(dateTo));
    }
    // Pill filters compose with search, matching the operator Bookings and Clients pages.
    if (filters.passenger) p.passenger = filters.passenger;
    if (filters.state?.length) p.state = filters.state;
    // Same stage, different shape: this endpoint filters by raw job status, so expand the group
    // rather than sending its name.
    if (filters.status) p.status = getStatusesForGroup(filters.status);
    return p;
  }, [
    page,
    pageSize,
    sort?.id,
    sort?.desc,
    clientId,
    search,
    isSearching,
    dateFrom,
    dateTo,
    filters.passenger,
    filters.state,
    filters.status,
  ]);
  // refetchOnMountOrArgChange: the operator's OBO create/edit happens on a sibling booking route and runs
  // through clientBookingApi (tag "ClientBookings"), which never invalidates this provider-job query
  // (tag "Booking"). Without this, returning to the client's Details tab shows a stale history (missing
  // the new row / not-yet-updated status) until the 60s cache expires. Remount → refetch keeps it fresh.
  const operatorQuery = useGetClientJobsQuery(operatorParams, { skip: !isOperator, refetchOnMountOrArgChange: true });

  // The active token-scoped /v2/cust/booking query: managed (merged) or self. Operator uses its own.
  const selfQuery = managed ? managedQuery : clientQuery;

  const isLoading = isOperator ? operatorQuery.isLoading : selfQuery.isLoading || (managed && accessQuery.isLoading);
  const isFetching = isOperator ? operatorQuery.isFetching : selfQuery.isFetching;

  const items = useMemo(
    () => (isOperator ? (operatorQuery.data?.data.bookings ?? []).map(toClientBooking) : selfQuery.data?.items ?? []),
    [isOperator, operatorQuery.data, selfQuery.data]
  );

  // Row open/replicate scoping — three OBO cases, else plain self-service on the default client-portal
  // path:
  //  1. Operator client-record rows stay inside the operator shell, scoped to the client.
  //  2. EA Manage-Client takeover (DEV-1296, `:accessId` in the URL + client scope) routes into the
  //     assistant-scoped booking sheet with the managed account_uuid.
  //  3. EA managed list (DEV-405): rows span every managed client, so the scope is resolved PER ROW from
  //     the owning client's CONNECTED access link; the EA's own rows keep the self path.
  // Without the EA branches a managed booking would open under the EA's own token
  // (`/client/booking/:jobId` + the EA's account_uuid), which the backend rejects — the EA holds a grant
  // to the client, not to itself — and the detail/document fetches 403.
  const tableData = useMemo(() => {
    if (managed) {
      return generateBookingHistoryData(items, (booking) => {
        const owner = booking.managedAccount?.uuid;
        if (!owner || owner === activeAccountId) return {};
        const linkAccessId = accessIdByClient.get(owner);
        // No CONNECTED link for this owner (still loading, or revoked since the list was fetched): fall
        // back to the self path, which is what every managed row did before per-row scoping.
        if (!linkAccessId) return {};
        return { bookingBasePath: `/client/book-for/${linkAccessId}`, accountId: owner };
      });
    }
    const scope =
      isOperator && operatorId && clientId
        ? { bookingBasePath: `/operator/${operatorId}/clients/${clientId}/booking`, accountId: clientId }
        : accessId && scopedAccountId
        ? { bookingBasePath: `/client/book-for/${accessId}`, accountId: scopedAccountId }
        : {};
    return generateBookingHistoryData(items, scope);
  }, [items, managed, activeAccountId, accessIdByClient, isOperator, operatorId, clientId, accessId, scopedAccountId]);

  const totalItems = isOperator ? operatorQuery.data?.data.page.total ?? 0 : selfQuery.data?.totalItems ?? 0;
  const pageCount = isOperator ? operatorQuery.data?.data.page.count ?? 1 : selfQuery.data?.pageCount ?? 1;

  // Managed mode inserts the owning-client "Client" column right after Passenger; other modes are
  // untouched (DEV-405).
  const columns = useMemo(() => {
    if (!managed) return bookingHistoryColumns;
    const passengerIdx = bookingHistoryColumns.findIndex((column) => column.id === "passenger");
    return [...bookingHistoryColumns.slice(0, passengerIdx + 1), managedClientColumn, ...bookingHistoryColumns.slice(passengerIdx + 1)];
  }, [managed]);

  const handleDatesChange = (dates?: Date[]) => {
    if (dates && dates[0]) setDateFrom(dates[0]);
    if (dates && dates[1]) setDateTo(dates[1]);
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Desktop/Tablet Header */}
      <div className="hidden md:block">
        <Typography variant="h2">{managed ? "All Managed Bookings" : "Booking History"}</Typography>
        {managed && (
          <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
            View a complete list of all bookings, including those from clients you manage and your own bookings.
          </Typography>
        )}
      </div>

      {/* Desktop/Tablet Controls */}
      <div className="hidden items-center justify-between gap-3 md:flex">
        <DateRangePicker
          dates={[dateFrom, dateTo]}
          onDatesChange={handleDatesChange}
          preset="thisMonth"
          size="sm"
          containerClassName="min-w-0 max-w-[320px]"
          autoPlacement
          // Block date changes while a query is in flight — rapidly toggling presets/arrows before
          // the previous fetch resolves left the table on a stale/empty state (out-of-order params).
          disabled={isFetching}
        />

        {/* Search + Filter */}
        <div className="flex shrink-0 items-center gap-2">
          <SearchInput
            placeholder="Search Job ID"
            search={search}
            onSearch={(val) => setSearch(val ?? "")}
            onClear={() => setSearch("")}
            size="sm"
            className="w-[180px] [&_input]:!min-w-0"
          />
          <BookingHistoryFilters filters={filters} onFilterChange={setFilters} />
        </div>
      </div>

      {/* Mobile Header + Controls */}
      <div className="flex items-center justify-between md:hidden">
        {mobileSearchOpen ? (
          <SearchInput
            placeholder="Enter Job Number or Search Anything"
            search={search}
            onSearch={(val) => setSearch(val ?? "")}
            onClear={() => {
              setSearch("");
              setMobileSearchOpen(false);
            }}
            size="sm"
            className="w-full [&_input]:!min-w-0"
          />
        ) : (
          <>
            <Typography variant="h2">{managed ? "All Managed Bookings" : "Booking History"}</Typography>
            <div className="flex items-center gap-1">
              <MobileDateDrawer dates={[dateFrom, dateTo]} onDatesChange={handleDatesChange} disabled={isFetching} />
              <BookingHistoryFilterDrawer filters={filters} onFilterChange={setFilters} />
              <IconButton iconName="SearchNormal1" variant="secondary" size="sm" onClick={() => setMobileSearchOpen(true)} />
            </div>
          </>
        )}
      </div>

      {/* Active Filter Pills */}
      <ActiveFilterPills filters={filters} onFilterChange={setFilters} />

      {/* Table */}
      <div className={`rounded-lg border border-neutral-mid-gray ${tableData.length > 0 ? "overflow-x-auto" : "overflow-hidden"}`}>
        {isLoading ? (
          <div className="bg-white p-8">
            <Loading />
          </div>
        ) : (
          <div className="relative">
            {isFetching && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
                <Loading />
              </div>
            )}
            <DataTable
              data={tableData}
              columns={columns}
              sorting={sorting}
              setSorting={setSorting}
              emptyState={<div />}
              className={`!rounded-none !ring-0 [&_tbody]:bg-white [&_tbody_tr:hover]:bg-neutral-gray [&_thead]:border-b [&_thead]:border-neutral-mid-gray ${
                tableData.length === 0 ? "[&_tbody_tr:only-child]:hidden" : ""
              }`}
            />
            {tableData.length === 0 && !isLoading && (
              <div className="bg-white">
                <BookingHistoryEmptyState />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <Pagination
          className="!mt-0"
          totalItems={totalItems}
          pageCount={pageCount}
          currentPage={page}
          pageSize={pageSize}
          onPageClick={setPage}
          onSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      )}
    </div>
  );
};
